<template>
  <div class="hris-login-page">
    <div class="hris-login-shell">
      <div class="hris-card hris-login-card">
        <div class="hris-login-header">
          <img class="hris-login-logo" src="../assets/sgcc-black.png" alt="BEAPAYDAY">
          <h1 class="hris-login-title">Sign in to your account</h1>
        </div>

        <div class="hris-login-form-container">
          <!-- Login -->
          <q-form v-if="!showOTP && !showAlternativeLogin" class="hris-login-form" ref="vForm" @submit="onSubmit">
            <div class="hris-form-field">
              <q-input
                id="email"
                name="email"
                type="text"
                autocomplete="email"
                outlined
                dense
                color="green"
                label="Username or Email"
                :disable="isSubmitted"
                v-model="login"
                lazy-rules
                :rules="[formRules.required]"
              ></q-input>
            </div>

            <div class="hris-form-field">
              <q-input
                id="password"
                name="password"
                type="password"
                autocomplete="current-password"
                outlined
                dense
                color="green"
                label="Password"
                :disable="isSubmitted"
                v-model="password"
                lazy-rules
                :rules="[formRules.required]"
              ></q-input>
              <p class="hris-login-error" v-if="isInvalid">*Invalid Credentials</p>
            </div>

            <div class="hris-login-submit">
              <q-btn unelevated class="hris-q-primary hris-login-btn" label="Log in" :loading="isSubmitted" type="submit"></q-btn>
            </div>
          </q-form>

          <!-- OTP -->
          <q-form v-else-if="showOTP && !showAlternativeLogin" class="hris-login-form" @submit="verifyOTP">
            <div class="hris-form-field">
              <q-input
                id="otp"
                name="otp"
                type="text"
                maxlength="6"
                autocomplete="one-time-code"
                outlined
                dense
                color="green"
                label="Enter Verification Code"
                v-model="otpCode"
                lazy-rules
                :rules="[formRules.required]"
              ></q-input>
              <p class="hris-login-error" v-if="otpInvalid">*Invalid OTP</p>
            </div>

            <div class="hris-login-submit">
              <q-btn unelevated class="hris-q-primary hris-login-btn" label="Verify OTP" :loading="isSubmitted" type="submit"></q-btn>
            </div>

            <div class="hris-login-resend">
              <q-btn flat label="Resend Code" @click="resendOTP"></q-btn>
            </div>
          </q-form>

          <!-- Alternative Login -->
          <q-form v-if="showAlternativeLogin" class="hris-login-form" @submit="logInViaEmployeeID">
            <div class="hris-form-field">
              <q-input
                id="employee-no"
                name="employee-no"
                type="text"
                outlined
                dense
                color="green"
                label="Enter Employee ID"
                v-model="employeeId"
                lazy-rules
                :rules="[formRules.required]"
              ></q-input>
              <p class="hris-login-error" v-if="employeeIdInvalid">*Invalid Employee ID</p>
            </div>

            <div class="hris-login-submit">
              <q-btn unelevated class="hris-q-primary hris-login-btn" label="Login" :loading="isSubmitted" type="submit"></q-btn>
            </div>
          </q-form>

          <div class="hris-login-footer" v-if="!showOTP && !showAlternativeLogin">
            <a class="hris-login-link" @click="showAlternativeLogin = true">Login with Employee ID</a>
          </div>
          <div class="hris-login-footer" v-if="!showOTP && showAlternativeLogin">
            <a class="hris-login-link" @click="showAlternativeLogin = false">Login with username or email</a>
          </div>
        </div>
      </div>

      <div class="hris-login-stores">
        <a href="https://apps.apple.com/il/app/beapayday/id6759442549" @click="apple = true">
          <img src="../assets/appstore.png" alt="Download on the AppStore" height="40">
        </a>
        <a href="https://play.google.com/store/apps/details?id=com.je310Solution.hrisbeapay" target="_blank">
          <img src="../assets/play_store.png" alt="Get on Google Play" height="40">
        </a>
      </div>

      <div class="hris-login-contact">
        <a href="/privacy-policy">
          Privacy Policy
        </a>
        <p class="hris-login-address">
          3A PG Building, West Avenue,<br>
          Barangay Paltok, Quezon City, Philippines
        </p>

        <p class="hris-login-phone">
          <span class="hris-login-phone-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M14.3308 15.9402L15.6608 14.6101C15.8655 14.403 16.1092 14.2384 16.3778 14.1262C16.6465 14.014 16.9347 13.9563 17.2258 13.9563C17.517 13.9563 17.8052 14.014 18.0739 14.1262C18.3425 14.2384 18.5862 14.403 18.7908 14.6101L20.3508 16.1702C20.5579 16.3748 20.7224 16.6183 20.8346 16.887C20.9468 17.1556 21.0046 17.444 21.0046 17.7351C21.0046 18.0263 20.9468 18.3146 20.8346 18.5833C20.7224 18.8519 20.5579 19.0954 20.3508 19.3L19.6408 20.02C19.1516 20.514 18.5189 20.841 17.8329 20.9541C17.1469 21.0672 16.4427 20.9609 15.8208 20.6501C10.4691 17.8952 6.11008 13.5396 3.35083 8.19019C3.03976 7.56761 2.93414 6.86242 3.04914 6.17603C3.16414 5.48963 3.49384 4.85731 3.99085 4.37012L4.70081 3.65015C5.11674 3.23673 5.67937 3.00464 6.26581 3.00464C6.85225 3.00464 7.41488 3.23673 7.83081 3.65015L9.40082 5.22021C9.81424 5.63615 10.0463 6.19871 10.0463 6.78516C10.0463 7.3716 9.81424 7.93416 9.40082 8.3501L8.0708 9.68018C8.95021 10.8697 9.91617 11.9926 10.9608 13.04C11.9994 14.0804 13.116 15.04 14.3008 15.9102L14.3308 15.9402Z"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span>+63 952 469 7039</span>
        </p>
      </div>
    </div>

    <q-dialog v-model="prompt" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Enter your employee ID</h2>
        </div>
        <div class="hris-modal-body">
          <q-input dense outlined color="green" v-model="employeeid" autofocus label="Employee ID"></q-input>
        </div>
        <div class="hris-modal-footer">
          <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup></q-btn>
          <q-btn unelevated class="hris-q-primary" label="Submit" @click="forgotPassword"></q-btn>
        </div>
      </div>
    </q-dialog>

    <q-dialog v-model="apple" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">How to download iOS app</h2>
        </div>
        <div class="hris-modal-body">
          <q-list padding dense class="rounded-borders">
            <q-item clickable v-ripple>
              <q-item-section class="text-red">Request a redemption code for IT personnel.</q-item-section>
            </q-item>
            <q-item clickable v-ripple>
              <q-item-section>1. Open your App Store application.</q-item-section>
            </q-item>
            <q-item clickable v-ripple>
              <q-item-section>2. Click your Account.</q-item-section>
            </q-item>
            <q-item clickable v-ripple>
              <q-item-section>3. Click "Redeem Gift Card or Code".</q-item-section>
            </q-item>
            <q-item clickable v-ripple>
              <q-item-section>4. Click "Enter Code Manually".</q-item-section>
            </q-item>
            <q-item clickable v-ripple>
              <q-item-section>5. Enter CODE and click "Redeem" button.</q-item-section>
            </q-item>
          </q-list>
        </div>
        <div class="hris-modal-footer">
          <q-btn unelevated class="hris-q-outline" label="Close" v-close-popup></q-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import vueCookies from 'vue-cookies';
export default {
  name: "Login",
  data: () => ({
    login: null,
    password: null,
    otpCode: null,
    userId: null,
    showOTP: false,
    isSubmitted: false,
    isInvalid: false,
    otpInvalid: false,
    showAlternativeLogin: false,
    // For alternative login
    employeeId: '',
    employeeIdInvalid: false,
    prompt: false,
    apple: false,
    employeeid: '',
    formRules: {
      required: val => !!val || 'Required'
    }
  }),
  methods: {
    // STEP 1: LOGIN
    async onSubmit() {
      this.isSubmitted = true;
      this.isInvalid = false;
      try {
        const res = await this.$store.dispatch('auth/login', {
          username: this.login.trim(),
          password: this.password.trim()
        });

        this.isSubmitted = false;
        if (res.requires_2fa) {
          this.userId = res.user_id;
          this.showOTP = true;
        }
      } catch (err) {
        this.isSubmitted = false;
        this.isInvalid = true;
      }
    },

    // STEP 2: VERIFY OTP
    async verifyOTP() {
      if (!this.otpCode || this.otpCode.length !== 6) {
        this.otpInvalid = true;
        return;
      }
      this.isSubmitted = true;
      this.otpInvalid = false;

      try {
        await this.$store.dispatch('auth/verifyOTP', {
          userId: this.userId,
          code: this.otpCode
        });
        this.isSubmitted = false;
      } catch (err) {
        this.isSubmitted = false;
        this.otpInvalid = true;
      }
    },

    resendOTP() {
      if (!this.userId) return;
      this.isSubmitted = true;
      axios.post(this.apiUrl + '/resend-otp', { user_id: this.userId })
        .then(() => { this.isSubmitted = false; })
        .catch(() => { this.isSubmitted = false; });
    },

    async logInViaEmployeeID() {
      this.isSubmitted = true;
      this.employeeIdInvalid = false;
      try {
        await this.$store.dispatch('auth/loginViaEmployeeId', {
          employeeId: this.employeeId.trim()
        });
      } catch (err) {
        this.employeeIdInvalid = true;
        this.isSubmitted = false;
      }
    }
  }
};
</script>

<style scoped>
.hris-login-page {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-family: var(--hris-font);
  background: linear-gradient(180deg, rgba(47, 143, 98, 0.07) 0%, rgba(47, 143, 98, 0.02) 45%, #fff 100%);
}

.hris-login-shell {
  width: 100%;
  max-width: 420px;
}

.hris-login-card {
  padding: 36px 34px;
  box-shadow: var(--hris-shadow-md);
}

.hris-login-header {
  text-align: center;
}

.hris-login-logo {
  max-width: 260px;
  width: 100%;
}

.hris-login-title {
  margin: 18px 0 4px;
  font-weight: 700;
  font-size: 1.375rem;
  letter-spacing: -0.02em;
  color: var(--hris-text);
}

.hris-login-form-container {
  margin-top: 26px;
}

.hris-login-form .hris-form-field {
  margin-bottom: 16px;
}

.hris-login-error {
  color: #a61b12;
  text-align: center;
  font-size: 0.8125rem;
  margin: 8px 0 0;
}

.hris-login-submit {
  margin-top: 6px;
}

.hris-login-btn {
  width: 100%;
}

.hris-login-resend {
  text-align: center;
  margin-top: 8px;
}

.hris-login-footer {
  text-align: center;
  margin-top: 22px;
  font-size: 0.875rem;
  color: var(--hris-text-secondary);
}

.hris-login-link {
  color: var(--hris-accent);
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.hris-login-link:hover {
  color: var(--hris-accent-hover);
  text-decoration: underline;
}

.hris-login-stores {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.hris-login-contact {
  position: fixed;
  bottom: 16px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  font-size: 0.75rem;
  z-index: 10;
}

.hris-login-contact a {
  color: var(--hris-text-secondary);
}

.hris-login-address {
  display: inline-flex;
  align-self: flex-end;
  padding: 6px 10px;
  border-radius: var(--hris-radius-sm);
  line-height: 1.4;
  color: var(--hris-text-secondary);
  background: var(--hris-accent-soft);
  white-space: normal;
  margin: 0;
}

.hris-login-phone {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--hris-text-secondary);
  margin: 0;
}

.hris-login-phone-icon svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  stroke: currentColor;
  color: var(--hris-accent);
}

@media (max-width: 768px) {
  .hris-login-page {
    padding: 20px;
  }

  .hris-login-card {
    padding: 28px 22px;
  }

  .hris-login-contact {
    position: relative;
    bottom: auto;
    right: auto;
    margin-top: 2rem;
    width: 100%;
    align-items: center;
    text-align: center;
  }

  .hris-login-address {
    align-self: center;
    max-width: 90%;
  }

  .hris-login-phone {
    justify-content: center;
  }
}
</style>
